import { useState, useCallback, useRef } from 'react';
import type { ReadinessResult, DeepAnalysisResult, ReadinessInput } from './types';
import { analyzePrompt } from './rulesEngine';
import { runDeepAnalysis } from './deepAnalysisService';

interface UseSunoReadinessReturn {
  result: ReadinessResult | null;
  deepAnalysis: DeepAnalysisResult | null;
  isAnalyzing: boolean;
  deepError: string | null;
  analyze: (input: ReadinessInput) => ReadinessResult;
  requestDeepAnalysis: (input: ReadinessInput) => Promise<void>;
  clear: () => void;
}

export function useSunoReadiness(): UseSunoReadinessReturn {
  const [result, setResult] = useState<ReadinessResult | null>(null);
  const [deepAnalysis, setDeepAnalysis] = useState<DeepAnalysisResult | null>(null);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [deepError, setDeepError] = useState<string | null>(null);
  const abortRef = useRef(0);

  const analyze = useCallback((input: ReadinessInput): ReadinessResult => {
    const r = analyzePrompt(input);
    setResult(r);
    setDeepAnalysis(null);
    setDeepError(null);
    return r;
  }, []);

  const requestDeepAnalysis = useCallback(async (input: ReadinessInput) => {
    const id = ++abortRef.current;
    setIsAnalyzing(true);
    setDeepError(null);
    try {
      const deep = await runDeepAnalysis(input);
      if (abortRef.current === id) {
        setDeepAnalysis(deep);
      }
    } catch (err) {
      if (abortRef.current === id) {
        setDeepError(err instanceof Error ? err.message : 'Deep analysis failed');
      }
    } finally {
      if (abortRef.current === id) {
        setIsAnalyzing(false);
      }
    }
  }, []);

  const clear = useCallback(() => {
    abortRef.current++;
    setResult(null);
    setDeepAnalysis(null);
    setIsAnalyzing(false);
    setDeepError(null);
  }, []);

  return { result, deepAnalysis, isAnalyzing, deepError, analyze, requestDeepAnalysis, clear };
}
